import React, { useState, useMemo, useRef } from 'react';
import './MobileGroupsList.css';

function MobileGroupsList({ groups, donors, savedLists = [], savedTables = [], onSelectGroup, onSelectPerson, onSelectList, onSelectTable, onOpenCreateSheet }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const inputRef = useRef(null);

  // Calculate member counts for each group
  const groupsWithCounts = useMemo(() => {
    const donorList = Object.values(donors || {});

    return (groups || []).map(group => {
      const isInGroup = (d) => {
        if (d.group_ids && Array.isArray(d.group_ids) && d.group_ids.length > 0) {
          return d.group_ids.some(id => String(id) === String(group.id));
        }
        return d.groupId === group.id || d.group_id === group.id;
      };
      const members = donorList.filter(d => isInGroup(d));
      const memberCount = members.length;

      // Calculate total amount for the group
      const totalAmount = members.reduce((sum, d) => {
        const amt = parseFloat(String(d.amount || '0').replace(/[^0-9.]/g, '')) || 0;
        return sum + amt;
      }, 0);

      return {
        ...group,
        memberCount,
        totalAmount
      };
    }).sort((a, b) => a.name.localeCompare(b.name));
  }, [groups, donors]);

  // Filter by search (for main list)
  const filteredGroups = useMemo(() => {
    if (!searchQuery.trim()) return groupsWithCounts;
    const query = searchQuery.toLowerCase();
    return groupsWithCounts.filter(g => g.name.toLowerCase().includes(query));
  }, [groupsWithCounts, searchQuery]);

  // Universal search results
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return null;
    const q = searchQuery.toLowerCase();

    // Search people by name, narrative, and fields
    const donorEntries = Object.entries(donors || {});
    const people = donorEntries
      .filter(([name, data]) => {
        if (name.toLowerCase().includes(q)) return true;
        if (data.narrative?.toLowerCase().includes(q)) return true;
        // Search custom fields
        if (data.fields) {
          return Object.values(data.fields).some(v =>
            String(v).toLowerCase().includes(q)
          );
        }
        return false;
      })
      .slice(0, 5)
      .map(([name, data]) => ({ name, ...data }));

    // Search groups by name
    const groupResults = groupsWithCounts
      .filter(g => g.name.toLowerCase().includes(q))
      .slice(0, 5);

    // Search lists by name or organization prompt
    const listResults = savedLists
      .filter(l =>
        l.name?.toLowerCase().includes(q) ||
        l.organizationPrompt?.toLowerCase().includes(q)
      )
      .slice(0, 3);

    // Search tables by name or columns
    const tableResults = savedTables
      .filter(t =>
        t.name?.toLowerCase().includes(q) ||
        t.columns?.some(c => c.toLowerCase().includes(q))
      )
      .slice(0, 3);

    const hasResults = people.length > 0 || groupResults.length > 0 ||
      listResults.length > 0 || tableResults.length > 0;

    return hasResults ? { people, groups: groupResults, lists: listResults, tables: tableResults } : null;
  }, [searchQuery, donors, groupsWithCounts, savedLists, savedTables]);

  // Generate consistent color for group (cool colors only)
  const getGroupColor = (name) => {
    const colors = ['#3b82f6', '#8b5cf6', '#06b6d4', '#10b981', '#6366f1', '#14b8a6', '#0ea5e9'];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  // Get avatar color
  const getAvatarColor = (name) => {
    const colors = ['#a78bfa', '#60a5fa', '#34d399', '#2dd4bf', '#818cf8', '#22d3d1'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  // Get initials
  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Handle search result click
  const handleResultClick = (type, item) => {
    setSearchQuery('');
    setSearchFocused(false);
    inputRef.current?.blur();

    if (type === 'person' && onSelectPerson) {
      onSelectPerson(item);
    } else if (type === 'group' && onSelectGroup) {
      onSelectGroup(item);
    } else if (type === 'list' && onSelectList) {
      onSelectList(item);
    } else if (type === 'table' && onSelectTable) {
      onSelectTable(item);
    }
  };

  const showDropdown = searchFocused && searchQuery.trim();

  // Format amount for display
  const formatAmount = (amount) => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `$${Math.round(amount / 1000)}K`;
    } else if (amount > 0) {
      return `$${amount}`;
    }
    return null;
  };

  return (
    <div className="mobile-groups-list">
      {/* Header */}
      <div className="groups-header">
        <h1 className="groups-title">Groups</h1>
      </div>

      {/* Search */}
      <div className="groups-search-container">
        <div className={`groups-search ${showDropdown ? 'focused' : ''}`}>
          <svg className="groups-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            placeholder="Search everything..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
          />
          {searchQuery && (
            <button className="groups-search-clear" onClick={() => setSearchQuery('')}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Search dropdown */}
        {showDropdown && (
          <div className="groups-search-dropdown">
            {!searchResults ? (
              <div className="search-dropdown-empty">No results for "{searchQuery}"</div>
            ) : (
              <>
                {/* People */}
                {searchResults.people.length > 0 && (
                  <div className="search-dropdown-section">
                    <div className="search-dropdown-label">People</div>
                    {searchResults.people.map(person => (
                      <button
                        key={person.name}
                        className="search-dropdown-item"
                        onClick={() => handleResultClick('person', person)}
                      >
                        <div className="search-dropdown-avatar" style={{ borderColor: getAvatarColor(person.name), color: getAvatarColor(person.name) }}>
                          {getInitials(person.name)}
                        </div>
                        <div className="search-dropdown-info">
                          <span className="search-dropdown-name">{person.name}</span>
                          {person.amount && person.amount !== 'Unknown' && (
                            <span className="search-dropdown-meta">{person.amount}</span>
                          )}
                        </div>
                        <span className="search-dropdown-arrow">›</span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Groups */}
                {searchResults.groups.length > 0 && (
                  <div className="search-dropdown-section">
                    <div className="search-dropdown-label">Groups</div>
                    {searchResults.groups.map(group => (
                      <button
                        key={group.id}
                        className="search-dropdown-item"
                        onClick={() => handleResultClick('group', group)}
                      >
                        <div className="search-dropdown-icon" style={{ background: group.color || getGroupColor(group.name) }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                            <circle cx="9" cy="7" r="4" />
                          </svg>
                        </div>
                        <div className="search-dropdown-info">
                          <span className="search-dropdown-name">{group.name}</span>
                          <span className="search-dropdown-meta">{group.memberCount} members</span>
                        </div>
                        <span className="search-dropdown-arrow">›</span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Lists */}
                {searchResults.lists.length > 0 && (
                  <div className="search-dropdown-section">
                    <div className="search-dropdown-label">Lists</div>
                    {searchResults.lists.map(list => (
                      <button
                        key={list.id}
                        className="search-dropdown-item"
                        onClick={() => handleResultClick('list', list)}
                      >
                        <div className="search-dropdown-icon list-icon">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                            <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
                          </svg>
                        </div>
                        <div className="search-dropdown-info">
                          <span className="search-dropdown-name">{list.name || list.organizationPrompt}</span>
                          <span className="search-dropdown-meta">List</span>
                        </div>
                        <span className="search-dropdown-arrow">›</span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Tables */}
                {searchResults.tables.length > 0 && (
                  <div className="search-dropdown-section">
                    <div className="search-dropdown-label">Tables</div>
                    {searchResults.tables.map(table => (
                      <button
                        key={table.id}
                        className="search-dropdown-item"
                        onClick={() => handleResultClick('table', table)}
                      >
                        <div className="search-dropdown-icon table-icon">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                            <rect x="3" y="3" width="18" height="18" rx="2" />
                            <path d="M3 9h18M9 21V9" />
                          </svg>
                        </div>
                        <div className="search-dropdown-info">
                          <span className="search-dropdown-name">{table.name}</span>
                          <span className="search-dropdown-meta">Table</span>
                        </div>
                        <span className="search-dropdown-arrow">›</span>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Groups list */}
      <div className="groups-scroll">
        {filteredGroups.length === 0 && !searchQuery ? (
          <div className="groups-empty">
            <div className="groups-empty-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </div>
            <p className="groups-empty-text">No groups yet</p>
            <p className="groups-empty-hint">Create a group to organize your people</p>
          </div>
        ) : filteredGroups.length === 0 ? (
          <div className="groups-empty">
            <p className="groups-empty-text">No groups matching "{searchQuery}"</p>
          </div>
        ) : (
          <>
            <div className="groups-section-label">Your Groups</div>
            {filteredGroups.map(group => (
              <button
                key={group.id}
                className="groups-row"
                onClick={() => onSelectGroup(group)}
              >
                <div className="groups-row-left">
                  <div
                    className="groups-row-dot"
                    style={{ background: group.color || getGroupColor(group.name) }}
                  />
                  <span className="groups-row-name">{group.name}</span>
                </div>
                <div className="groups-row-right">
                  {group.totalAmount > 0 && (
                    <span className="groups-row-amount">{formatAmount(group.totalAmount)}</span>
                  )}
                  <span className="groups-row-count">{group.memberCount}</span>
                  <span className="groups-row-arrow">›</span>
                </div>
              </button>
            ))}
          </>
        )}

        {/* Add group button */}
        <button
          className="groups-add-btn"
          onClick={onOpenCreateSheet}
        >
          <span className="groups-add-icon">+</span>
          <span>New group</span>
        </button>
      </div>
    </div>
  );
}

export default MobileGroupsList;
